import {
  type GetApiTagsQueryParams,
  type TagModel,
  type TagModelPagedResult,
  getApiTags,
} from '@/shared/api'
import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@/shared/lib/query'
import { type Query, createQuery } from '@farfetched/core'
import {
  type EventCallable,
  type Store,
  type StoreWritable,
  combine,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { debounce } from 'patronum'

export interface TagsQueryModelBase {
  // Availability
  $isAvailable: StoreWritable<boolean>
  availableSet: EventCallable<void>
  unavailableSet: EventCallable<void>
  //   Filters
  $filters: StoreWritable<GetApiTagsQueryParams>
  filtersInit: EventCallable<Partial<GetApiTagsQueryParams>>
  filtersChanged: EventCallable<Partial<GetApiTagsQueryParams>>
  filtersReset: EventCallable<void>
  //   Search
  $search: StoreWritable<string | null>
  searchChanged: EventCallable<string>
  searchReset: EventCallable<void>
  //   Params
  $params: Store<{
    params: GetApiTagsQueryParams
  }>
  // Query
  query: Query<
    {
      params?: GetApiTagsQueryParams
    },
    TagModelPagedResult,
    unknown
  >
  refreshed: EventCallable<void>
  // Accumulated
  $accumulatedData: StoreWritable<TagModel[]>
  accumulatedDataReset: EventCallable<void>
}

export interface TagsQueryModelWithPagination extends TagsQueryModelBase {
  $page: StoreWritable<number>
  pageIncreased: EventCallable<void>
  pageChanged: EventCallable<number>
  pageReset: EventCallable<void>
  $pageSize: StoreWritable<number>
  pageSizeChanged: EventCallable<number>
}

interface TagsQueryModelParams {
  initialFilters?: Partial<GetApiTagsQueryParams>
  usePagination: boolean
  accumulate: boolean
}

export function createTagsQueryModel(
  {
    initialFilters = {},
    usePagination = true,
    accumulate = false,
  }: TagsQueryModelParams = {
    initialFilters: {},
    usePagination: true,
    accumulate: false,
  },
): TagsQueryModelWithPagination | TagsQueryModelBase {
  // Query
  const query = createQuery({ handler: getApiTags })

  // Availability
  const availableSet = createEvent()
  const unavailableSet = createEvent()
  const $isAvailable = createStore(false)
    .on(availableSet, () => true)
    .on(unavailableSet, () => false)

  // Filters
  const filtersInit = createEvent<Partial<GetApiTagsQueryParams>>()
  const filtersChanged = createEvent<Partial<GetApiTagsQueryParams>>()
  const filtersReset = createEvent()
  const $filters = createStore<GetApiTagsQueryParams>(initialFilters)
    .on(filtersInit, (_, payload) => ({ ...initialFilters, ...payload }))
    .on(filtersChanged, (state, payload) => ({ ...state, ...payload }))
    .on(filtersReset, () => initialFilters)

  // Search
  const searchChanged = createEvent<string>()
  const searchReset = createEvent()
  const $search = createStore<string | null>(null)

  // Pagination

  const $page = createStore<number>(DEFAULT_PAGE)

  const pageIncreased = createEvent()
  const pageChanged = createEvent<number>()
  const pageReset = createEvent()
  $page
    .on(pageChanged, (_, page) => Math.max(DEFAULT_PAGE, page))
    .on(pageReset, () => DEFAULT_PAGE)

  const $pageSize = createStore<number>(DEFAULT_PAGE_SIZE)
  const pageSizeChanged = createEvent<number>()
  $pageSize.on(pageSizeChanged, (_, size) => size)

  // Query params
  const $params = combine(
    $filters,
    $search,
    $page,
    $pageSize,
    (filters, search, page, pageSize) => {
      const params: GetApiTagsQueryParams = { ...filters }
      if (search) params.SearchQuery = search.trim()
      // Pagination
      if (usePagination) {
        params.Limit = pageSize
        params.Offset = (page - 1) * pageSize
      }
      return { params }
    },
  )

  // Accumulation
  const initialAccumulatedData: TagModel[] = []
  const accumulatedDataReset = createEvent()
  const $accumulatedData = createStore<TagModel[]>(initialAccumulatedData)
    .on(query.finished.success, (state, { result }) => {
      if (accumulate) {
        const map = new Map(state.map((item) => [item.id, item]))
        for (const item of result.items) {
          map.set(item.id, item)
        }
        return Array.from(map.values())
      }
      return result.items
    })
    .on(accumulatedDataReset, () => initialAccumulatedData)

  sample({
    clock: [filtersInit, filtersReset],
    target: accumulatedDataReset,
  })

  // Start
  // Related only to forms, move it to the form model
  // Check model factories for fields with lazy load in other forms. do the same to these kind of samples
  sample({
    source: query.$status,
    clock: accumulatedDataReset,
    filter: (status) => status !== 'initial',
    target: pageReset,
  })
  //End

  // Debounce
  const paramsDebounced = debounce({
    source: $params,
    timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  })

  const searchChangedDebounced = debounce({
    source: searchChanged,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  const searchResetDebounced = debounce({
    source: searchReset,
    timeout: DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
  })

  // Pagination
  sample({
    clock: pageIncreased,
    source: {
      page: $page,
      pageSize: $pageSize,
      total: query.$data.map((d) => d?.totalItems ?? 0),
      accumulated: $accumulatedData,
      pending: query.$pending,
    },
    filter: ({ page, pageSize, total, accumulated, pending }) => {
      const maxPage = Math.ceil(total / pageSize)
      const nextPage = page + 1
      const hasEnoughAccumulated = accumulated.length >= page * pageSize
      return !pending && nextPage <= maxPage && hasEnoughAccumulated
    },
    fn: ({ page }) => page + 1,
    target: pageChanged,
  })
  sample({
    source: query.$status,
    clock: [$filters, $search],
    filter: (status) => status !== 'initial',
    target: pageReset,
  })

  // Start query
  sample({
    clock: paramsDebounced,
    filter: $isAvailable,
    target: query.start,
  })

  //   Refresh
  const refreshed = createEvent()
  sample({
    source: $params,
    clock: refreshed,
    filter: $isAvailable,
    target: query.start,
  })

  // Change search

  $search
    .on(searchChangedDebounced, (_, search) => search)
    .reset(searchResetDebounced)

  // Reset logic
  sample({
    clock: unavailableSet,
    target: query.reset,
  })

  sample({
    clock: query.reset,
    target: [filtersReset, searchReset, pageReset, accumulatedDataReset],
  })

  const model: TagsQueryModelBase = {
    // Events
    availableSet,
    unavailableSet,
    filtersInit,
    filtersChanged,
    filtersReset,
    searchChanged,
    searchReset,
    refreshed,
    // Stores
    $isAvailable,
    $filters,
    $search,
    $params,
    // Query
    query,
    // Accumulated
    $accumulatedData,
    accumulatedDataReset,
  }

  const modelWithPagination: TagsQueryModelWithPagination = {
    ...model,
    $page,
    pageIncreased,
    pageChanged,
    pageReset,
    $pageSize,
    pageSizeChanged,
  }

  if (usePagination) {
    return modelWithPagination
  }

  return model
}
